import { useState, useEffect } from "react";
import { images } from "../../constants/images";
import "../../assets/stylesheets/bootstrap.min.css";
import "../../assets/stylesheets/style.css";
import "../../assets/stylesheets/responsive.css";

export default function WebApplicationDevelopment() {
  useEffect(() => {
    document.title = "Custom Web Application Development | Plural Code";
    window.scrollTo(0, 0);
  }, []);

  const heroStats = [
    { value: "68%", label: "Faster Development" },
    { value: "95%", label: "Client Satisfaction" },
    { value: "40%", label: "Cost Savings" },
    { value: "100+", label: "Projects Delivered" },
  ];

  const webAppTypes = [
    {
      icon: "👥",
      title: "Customer Portals",
      description:
        "Give clients 24/7 access to their information, orders, and documents",
      features: [
        "Secure login & authentication",
        "Document management",
        "Real-time status updates",
        "Communication tools",
        "Custom dashboards",
      ],
      bestFor: [
        "Professional services firms",
        "B2B companies",
        "Subscription businesses",
        "Service providers",
      ],
      example:
        "Law firm portal for case documents, billing, and secure communication",
    },
    {
      icon: "🌐",
      title: "SaaS Platforms",
      description:
        "Subscription-based software accessible via browser—your own software business",
      features: [
        "Multi-tenant architecture",
        "Subscription billing",
        "User management & roles",
        "API integrations",
        "Analytics & reporting",
      ],
      bestFor: [
        "Entrepreneurs launching products",
        "Businesses digitizing services",
        "Industry-specific tools",
        "Scalable startups",
      ],
      example:
        "Construction project management tool with contractor-specific features",
    },
    {
      icon: "🏢",
      title: "Internal Management Tools",
      description:
        "Replace spreadsheets with custom apps that streamline operations",
      features: [
        "Custom workflows",
        "Approval routing",
        "Data validation",
        "Automated notifications",
        "Reporting dashboards",
      ],
      bestFor: [
        "Operations teams",
        "HR departments",
        "Finance & accounting",
        "Complex workflow teams",
      ],
      example:
        "HR onboarding system managing documents, approvals, and training",
    },
    {
      icon: "🛒",
      title: "B2B/B2C Platforms",
      description:
        "Marketplaces, booking systems connecting buyers and sellers",
      features: [
        "User profiles & roles",
        "Search & filtering",
        "Booking management",
        "Rating systems",
        "Payment processing",
      ],
      bestFor: [
        "Marketplace businesses",
        "Booking/scheduling services",
        "Two-sided platforms",
        "Aggregator businesses",
      ],
      example:
        "Equipment rental marketplace with booking, payments, and reviews",
    },
    {
      icon: "📊",
      title: "Data Management & Visualization",
      description:
        "Turn complex data into actionable insights with custom dashboards",
      features: [
        "Real-time data visualization",
        "Custom KPIs & metrics",
        "Interactive charts",
        "Drill-down capabilities",
        "Automated reports",
      ],
      bestFor: [
        "Data-driven businesses",
        "Complex reporting needs",
        "Multiple metrics tracking",
        "Data-based decision teams",
      ],
      example:
        "Manufacturing dashboard with production metrics and predictive alerts",
    },
    {
      icon: "📱",
      title: "Progressive Web Apps (PWA)",
      description:
        "Web apps that work offline and feel like native mobile apps",
      features: [
        "Offline functionality",
        "Push notifications",
        "Home screen install",
        "Fast loading",
        "Native app feel",
      ],
      bestFor: [
        "Mobile-first businesses",
        "App-like experience needs",
        "Offline functionality",
        "Avoiding app stores",
      ],
      example: "Field service app for technicians with offline job logging",
    },
  ];

  const processSteps = [
    {
      step: 1,
      icon: "🔍",
      title: "Discovery & Planning",
      duration: "1-2 weeks",
      description: "Deep dive into your vision, users, and requirements",
      points: [
        "Define user types & roles",
        "Map user journeys",
        "Document features",
        "Identify integrations",
        "Define success metrics",
      ],
      deliverable: "Detailed requirements & project plan",
    },
    {
      step: 2,
      icon: "🎨",
      title: "UX/UI Design",
      duration: "2-3 weeks",
      description: "Designing intuitive, beautiful interfaces",
      points: [
        "Wireframes & mockups",
        "User flow design",
        "Responsive layouts",
        "Design system",
        "Interactive prototypes",
      ],
      deliverable: "Complete design specs & prototype",
    },
    {
      step: 3,
      icon: "🏗️",
      title: "Architecture & Database",
      duration: "1 week",
      description: "Building a solid foundation for scalability",
      points: [
        "System architecture",
        "Database schema",
        "API structure",
        "Security strategy",
        "Technology stack",
      ],
      deliverable: "Technical architecture document",
    },
    {
      step: 4,
      icon: "⚡",
      title: "Agile Development",
      duration: "6-12 weeks",
      description: "Building in 2-week sprints with weekly demos",
      points: [
        "Frontend development",
        "Backend development",
        "API development",
        "Database implementation",
        "Weekly feedback",
      ],
      deliverable: "Working application, feature by feature",
    },
    {
      step: 5,
      icon: "🧪",
      title: "Testing & QA",
      duration: "2 weeks",
      description: "Ensuring rock-solid quality and performance",
      points: [
        "Functional testing",
        "Cross-browser testing",
        "Performance testing",
        "Security testing",
        "User acceptance",
      ],
      deliverable: "Production-ready application",
    },
    {
      step: 6,
      icon: "🚀",
      title: "Deployment & Launch",
      duration: "1 week",
      description: "Smooth deployment with monitoring and support",
      points: [
        "Cloud hosting setup",
        "Domain & SSL",
        "Production deployment",
        "Monitoring setup",
        "User training",
      ],
      deliverable: "Live web application + documentation",
    },
  ];

  const technologies = [
    {
      category: "Frontend",
      icon: "🎨",
      tech: [
        "React",
        "Modern JavaScript (ES6+)",
        "Responsive CSS",
        "Progressive enhancement",
      ],
    },
    {
      category: "Backend",
      icon: "⚙️",
      tech: [
        "Ruby on Rails/Django/Node.js",
        "PostgreSQL",
        "RESTful APIs",
        "Background jobs",
      ],
    },
    {
      category: "Infrastructure",
      icon: "☁️",
      tech: [
        "AWS/Cloud hosting",
        "CDN integration",
        "Automated backups",
        "SSL/HTTPS",
        "CI/CD pipelines",
      ],
    },
    {
      category: "Security",
      icon: "🔒",
      tech: [
        "OAuth 2.0 / JWT",
        "Role-based access",
        "SQL injection prevention",
        "XSS protection",
        "Security audits",
      ],
    },
  ];

  const faqs = [
    {
      question: "Can you build a web app that works on mobile devices?",
      answer:
        "Absolutely! All our web applications are fully responsive—they adapt beautifully to desktop, tablet, and mobile. We can also build Progressive Web Apps (PWA) that feel like native mobile apps.",
    },
    {
      question: "Can users access the web app offline?",
      answer:
        "For certain features, yes. We implement Progressive Web App (PWA) technology allowing offline functionality with automatic data syncing when connection returns.",
    },
    {
      question: "How do you handle security?",
      answer:
        "Security is built-in from day one: encrypted connections (SSL), secure authentication, role-based access control, protection against common vulnerabilities, and regular security audits.",
    },
    {
      question: "Can the web app integrate with other tools we use?",
      answer:
        "Yes. We build custom integrations with payment processors, email services, CRMs, accounting software, and virtually any tool with an API.",
    },
    {
      question: "What if we need to scale to thousands of users?",
      answer:
        "We architect web apps with scalability in mind from the start. Our cloud infrastructure can grow with you—from 10 users to 10,000+ without performance issues.",
    },
    {
      question: "Who owns the code and application?",
      answer:
        "You do. Complete ownership of all code, designs, and intellectual property. No vendor lock-in.",
    },
    {
      question: "Can we add features later?",
      answer:
        "Definitely. Web applications evolve. We can add features, modify workflows, or expand functionality as your needs change.",
    },
    {
      question: "How do you handle ongoing maintenance?",
      answer:
        "We offer support plans including bug fixes, security updates, performance monitoring, and feature enhancements. Or you can take over maintenance with your own team.",
    },
  ];

  return (
    <div>
      <main className="wrapper">
        {/* HERO SECTION */}
        <section
          style={{
            minHeight: "90vh",
            display: "flex",
            alignItems: "center",
            position: "relative",
            overflow: "hidden",
            background: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
          }}
        >
          {/* Background Elements */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `
              radial-gradient(circle at 20% 80%, rgba(255, 215, 0, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.05) 0%, transparent 50%),
              radial-gradient(circle at 40% 40%, rgba(10, 36, 88, 0.2) 0%, transparent 50%)
            `,
            }}
          />

          <div className="container">
            <div className="row align-items-center min-vh-90">
              <div className="col-lg-6 col-md-12 mb-5 mb-lg-0">
                <div
                  className="text-white position-relative"
                  style={{ zIndex: 2 }}
                >
                  {/* Badge */}
                  <div
                    className="d-inline-flex align-items-center gap-2 px-3 py-2 rounded-pill mb-4"
                    style={{
                      background: "rgba(255, 215, 0, 0.15)",
                      backdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 215, 0, 0.3)",
                    }}
                  >
                    <span style={{ fontSize: "20px" }}>🌐</span>
                    <span
                      className="fw-bold"
                      style={{ fontSize: "14px", color: "#FFD700" }}
                    >
                      CUSTOM WEB APPLICATIONS
                    </span>
                  </div>

                  {/* Main Heading */}
                  <h1
                    className="display-3 fw-bold mb-4"
                    style={{
                      lineHeight: "1.1",
                      background:
                        "linear-gradient(to right, #FFFFFF 0%, #E6F0FF 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                      textShadow: "0 2px 10px rgba(0,0,0,0.1)",
                    }}
                  >
                    Custom Web Application
                    <span
                      className="d-block"
                      style={{
                        background:
                          "linear-gradient(90deg, #FFD700 0%, #FFEC8B 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                      }}
                    >
                      Development
                    </span>
                  </h1>

                  {/* Subtitle */}
                  <p
                    className="fs-5 mb-5"
                    style={{
                      color: "#E6F0FF",
                      lineHeight: "1.6",
                      maxWidth: "90%",
                      fontWeight: "300",
                    }}
                  >
                    Powerful, scalable web platforms built specifically for your
                    business needs—from customer portals to complete SaaS
                    solutions that drive growth and efficiency.
                  </p>

                  {/* CTA Buttons */}
                  <div className="d-flex flex-wrap gap-3 mb-5">
                    <button
                      onClick={() =>
                        window.open(
                          "https://topmate.io/pluralcode/1677497",
                          "_blank",
                        )
                      }
                      className="btn btn-lg px-5 py-3 d-flex align-items-center gap-2"
                      style={{
                        background:
                          "linear-gradient(135deg, #FFD700 0%, #FFC107 100%)",
                        border: "none",
                        color: "#0a2458",
                        fontWeight: "700",
                        borderRadius: "12px",
                        transition: "all 0.3s ease",
                        boxShadow: "0 10px 30px rgba(255, 215, 0, 0.3)",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = "translateY(-3px)";
                        e.target.style.boxShadow =
                          "0 15px 40px rgba(255, 215, 0, 0.4)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = "translateY(0)";
                        e.target.style.boxShadow =
                          "0 10px 30px rgba(255, 215, 0, 0.3)";
                      }}
                    >
                      <span>💬</span>
                      <span>Discuss Your Web App</span>
                    </button>

                    <button
                      onClick={() => window.open("/ourworks", "_blank")}
                      className="btn btn-lg px-5 py-3 d-flex align-items-center gap-2"
                      style={{
                        background: "transparent",
                        border: "2px solid #FFD700",
                        color: "#FFD700",
                        fontWeight: "600",
                        borderRadius: "12px",
                        transition: "all 0.3s ease",
                        backdropFilter: "blur(10px)",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "rgba(255, 215, 0, 0.1)";
                        e.target.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.transform = "translateY(0)";
                      }}
                    >
                      <span>✨</span>
                      <span>See Our Work</span>
                    </button>
                  </div>

                  {/* Stats */}
                  <div className="d-flex flex-wrap gap-4">
                    {heroStats.map((stat, index) => (
                      <div key={index} className="text-center">
                        <div
                          className="fw-bold"
                          style={{ fontSize: "2rem", color: "#FFD700" }}
                        >
                          {stat.value}
                        </div>
                        <div style={{ fontSize: "0.875rem", color: "#E6F0FF" }}>
                          {stat.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Hero Image */}
              <div className="col-lg-6 col-md-12">
                <div className="position-relative">
                  <div
                    style={{
                      position: "relative",
                      padding: "20px",
                    }}
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                        bottom: "0",
                        left: "0",
                        background:
                          "linear-gradient(135deg, rgba(255,215,0,0.15) 0%, rgba(255,215,0,0) 50%)",
                        borderRadius: "25px",
                        transform: "rotate(5deg)",
                        zIndex: 0,
                      }}
                    />

                    <div
                      style={{
                        position: "relative",
                        borderRadius: "20px",
                        overflow: "hidden",
                        boxShadow: "0 25px 50px rgba(0,0,0,0.4)",
                        transform:
                          "perspective(1000px) rotateY(-5deg) rotateX(2deg)",
                        transition: "transform 0.5s ease",
                        zIndex: 1,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform =
                          "perspective(1000px) rotateY(0deg) rotateX(0deg)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform =
                          "perspective(1000px) rotateY(-5deg) rotateX(2deg)";
                      }}
                    >
                      <img
                        src={images?.webApp || images?.aboutUs}
                        className="img-fluid w-100"
                        alt="Web Application Development Dashboard"
                        style={{
                          display: "block",
                          height: "auto",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IS A WEB APPLICATION */}
        <section
          style={{
            padding: "100px 0",
            background: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFF 100%)",
          }}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-lg-6 mb-5 mb-lg-0">
                <div style={{ position: "relative" }}>
                  <img
                    src={images?.whyBusinessNeedsWebApp}
                    className="img-fluid rounded-4"
                    alt="Web Application vs Website"
                    style={{
                      boxShadow: "0 20px 40px rgba(10, 36, 88, 0.1)",
                    }}
                  />
                  <div
                    style={{
                      position: "absolute",
                      top: "-20px",
                      left: "-20px",
                      width: "80px",
                      height: "80px",
                      background: "#FFD700",
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "32px",
                      boxShadow: "0 10px 20px rgba(255, 215, 0, 0.3)",
                    }}
                  >
                    🌐
                  </div>
                </div>
              </div>
              <div className="col-lg-6 ps-lg-5">
                <div className="mb-4">
                  <span
                    className="px-3 py-1 rounded-pill d-inline-block mb-3"
                    style={{
                      background: "rgba(10, 36, 88, 0.1)",
                      color: "#0a2458",
                      fontWeight: "600",
                      fontSize: "14px",
                    }}
                  >
                    WEB APP VS WEBSITE
                  </span>
                  <h2
                    className="tc-primary head-font mb-4"
                    style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
                  >
                    More Than Just a Website
                  </h2>
                  <p
                    className="head-font2 tc-primary fs-17 mb-4"
                    style={{ color: "#0A2458" }}
                  >
                    A web application is interactive software that runs in your
                    browser—think Gmail, Salesforce, or Trello. Unlike static
                    websites, web apps let users:
                  </p>
                </div>

                {[
                  {
                    icon: "🤝",
                    title: "Interact & transact",
                    desc: "Not just read, but DO things",
                  },
                  {
                    icon: "🗃️",
                    title: "Manage data",
                    desc: "Create, update, search, analyze",
                  },
                  {
                    icon: "👥",
                    title: "Collaborate",
                    desc: "Multiple users working together",
                  },
                  {
                    icon: "📱",
                    title: "Access anywhere",
                    desc: "Works on any device with internet",
                  },
                  {
                    icon: "⚡",
                    title: "Update in real-time",
                    desc: "Changes sync instantly",
                  },
                ].map((item, index) => (
                  <div
                    key={index}
                    className="d-flex gap-3 mb-4 p-3 rounded-3"
                    style={{
                      background:
                        index % 2 === 0
                          ? "rgba(10, 36, 88, 0.02)"
                          : "transparent",
                      borderLeft: `3px solid ${
                        index % 2 === 0 ? "#FFD700" : "#0a2458"
                      }`,
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateX(5px)";
                      e.currentTarget.style.background =
                        "rgba(10, 36, 88, 0.05)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateX(0)";
                      e.currentTarget.style.background =
                        index % 2 === 0
                          ? "rgba(10, 36, 88, 0.02)"
                          : "transparent";
                    }}
                  >
                    <div
                      className="flex-shrink-0"
                      style={{
                        fontSize: "24px",
                        width: "48px",
                        height: "48px",
                        background: "rgba(10, 36, 88, 0.1)",
                        borderRadius: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="head-font2 tc-primary fs-18 fw-bold mb-2">
                        {item.title}
                      </h3>
                      <p
                        className="head-font2 tc-primary fs-16 mb-0"
                        style={{ color: "#0A2458" }}
                      >
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}

                <div className="mt-5">
                  <h3 className="tc-primary head-font fs-24 mb-3">
                    Common Examples:
                  </h3>
                  <div className="row g-3">
                    {[
                      {
                        icon: "👥",
                        title: "Customer Portals",
                        desc: "Clients log in to view orders, documents, or project status",
                      },
                      {
                        icon: "🌐",
                        title: "SaaS Platforms",
                        desc: "Subscription-based software people access via browser",
                      },
                      {
                        icon: "🏢",
                        title: "Internal Tools",
                        desc: "Admin dashboards, management systems, workflow tools",
                      },
                      {
                        icon: "🛒",
                        title: "B2B/B2C Platforms",
                        desc: "Marketplaces, booking systems, service platforms",
                      },
                      {
                        icon: "📊",
                        title: "Data Management",
                        desc: "Custom databases, reporting tools, analytics platforms",
                      },
                    ].map((example, index) => (
                      <div key={index} className="col-12">
                        <div
                          className="d-flex align-items-start gap-3 p-3 rounded-3"
                          style={{ background: "rgba(10, 36, 88, 0.02)" }}
                        >
                          <div style={{ fontSize: "20px", color: "#0a2458" }}>
                            {example.icon}
                          </div>
                          <div>
                            <strong
                              className="head-font2 fs-16"
                              style={{ color: "#0a2458" }}
                            >
                              {example.title}:
                            </strong>
                            <p
                              className="head-font2 fs-14 mb-0"
                              style={{ color: "#0A2458" }}
                            >
                              {example.desc}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* WHY CUSTOM WEB APPS */}
        <section style={{ padding: "100px 0", background: "#FFFFFF" }}>
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(255, 215, 0, 0.15)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                WHY CHOOSE CUSTOM
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Why Build a Custom Web Application?
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                Off-the-shelf solutions have limitations. Custom web apps give
                you competitive advantages that generic platforms can't match.
              </p>
            </div>

            <div className="row g-4">
              {[
                {
                  icon: "🎯",
                  title: "Unique Requirements",
                  description:
                    "Your business needs features and workflows that don't exist in pre-built platforms. Custom means exactly what you need.",
                },
                {
                  icon: "💰",
                  title: "Better Economics",
                  description:
                    "Stop paying per-user subscriptions forever. Own your platform and control costs as you scale.",
                },
                {
                  icon: "🚀",
                  title: "Competitive Advantage",
                  description:
                    "Your web app becomes a unique asset that competitors can't replicate—a true differentiator.",
                },
                {
                  icon: "🔗",
                  title: "Perfect Integration",
                  description:
                    "Connect seamlessly with your existing systems, tools, and workflows without limitations.",
                },
                {
                  icon: "⚡",
                  title: "Lightning Fast",
                  description:
                    "Optimized for your specific use case, without the bloat of generic features you don't need.",
                },
                {
                  icon: "📈",
                  title: "Scalable Growth",
                  description:
                    "Built to grow with your business, handling increased users and data without performance issues.",
                },
              ].map((item, index) => (
                <div className="col-lg-4 col-md-6" key={index}>
                  <div
                    className="h-100 p-4 rounded-4"
                    style={{
                      background: "white",
                      border: "1px solid rgba(10, 36, 88, 0.1)",
                      boxShadow: "0 4px 6px rgba(10, 36, 88, 0.05)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 24px rgba(10, 36, 88, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 6px rgba(10, 36, 88, 0.05)";
                    }}
                  >
                    <div
                      className="mb-4 rounded-3 d-inline-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "48px",
                        width: "80px",
                        height: "80px",
                        background: "rgba(10, 36, 88, 0.05)",
                      }}
                    >
                      {item.icon}
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-3">
                      {item.title}
                    </h3>
                    <p
                      className="head-font2 tc-primary fs-16 mb-0"
                      style={{ color: "#0A2458" }}
                    >
                      {item.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* TYPES OF WEB APPS */}
        <section
          style={{
            padding: "100px 0",
            background: "linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%)",
          }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(10, 36, 88, 0.1)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                WEB APPLICATIONS WE DEVELOP
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Custom Web Applications We Build
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                From customer portals to complete SaaS platforms, we build web
                applications that solve real business problems.
              </p>
            </div>

            <div className="row g-4">
              {webAppTypes.map((type, index) => (
                <div className="col-lg-6" key={index}>
                  <div
                    className="h-100 p-4 rounded-4"
                    style={{
                      background: "white",
                      border: "1px solid rgba(10, 36, 88, 0.1)",
                      boxShadow: "0 8px 16px rgba(10, 36, 88, 0.08)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 16px 32px rgba(10, 36, 88, 0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 16px rgba(10, 36, 88, 0.08)";
                    }}
                  >
                    <div className="d-flex align-items-start gap-3 mb-4">
                      <div
                        className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          fontSize: "32px",
                          width: "60px",
                          height: "60px",
                          background: "rgba(10, 36, 88, 0.1)",
                        }}
                      >
                        {type.icon}
                      </div>
                      <div>
                        <h3 className="tc-primary head-font fs-20 mb-1">
                          {type.title}
                        </h3>
                        <p
                          className="head-font2 tc-primary fs-14 mb-0"
                          style={{ color: "#0A2458" }}
                        >
                          {type.description}
                        </p>
                      </div>
                    </div>

                    <div className="row g-3 mb-4">
                      <div className="col-6">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "rgba(10, 36, 88, 0.02)" }}
                        >
                          <p
                            className="head-font2 tc-primary fs-13 fw-bold mb-2"
                            style={{ color: "#0a2458" }}
                          >
                            Perfect for:
                          </p>
                          <ul
                            className="head-font2 tc-primary fs-12 mb-0"
                            style={{ paddingLeft: "16px", color: "#0A2458" }}
                          >
                            {type.bestFor.map((item, i) => (
                              <li key={i}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="col-6">
                        <div
                          className="p-3 rounded-3"
                          style={{ background: "rgba(10, 36, 88, 0.02)" }}
                        >
                          <p
                            className="head-font2 tc-primary fs-13 fw-bold mb-2"
                            style={{ color: "#0a2458" }}
                          >
                            Key Features:
                          </p>
                          <ul
                            className="head-font2 tc-primary fs-12 mb-0"
                            style={{ paddingLeft: "16px", color: "#0A2458" }}
                          >
                            {type.features.map((feature, i) => (
                              <li key={i}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div
                      className="p-3 rounded-3"
                      style={{
                        background: "rgba(10, 36, 88, 0.03)",
                        border: "1px solid rgba(10, 36, 88, 0.1)",
                      }}
                    >
                      <p
                        className="head-font2 fs-13 mb-0"
                        style={{ fontStyle: "italic", color: "#0A2458" }}
                      >
                        <strong>Example:</strong> {type.example}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* OUR PROCESS */}
        <section style={{ padding: "100px 0", background: "#FFFFFF" }}>
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(255, 215, 0, 0.15)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                OUR PROCESS
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                How We Build Your Web Application
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                Web applications require careful planning, smart architecture,
                and user-centered design. Here's our proven 6-step process.
              </p>
            </div>

            <div className="row g-4">
              {processSteps.map((step, index) => (
                <div className="col-lg-6" key={index}>
                  <div
                    className="h-100 p-4 rounded-4 position-relative"
                    style={{
                      background: "white",
                      border: "1px solid rgba(10, 36, 88, 0.1)",
                      boxShadow: "0 8px 16px rgba(10, 36, 88, 0.08)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 16px 32px rgba(10, 36, 88, 0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 16px rgba(10, 36, 88, 0.08)";
                    }}
                  >
                    <div className="d-flex align-items-start gap-3 mb-4">
                      <div
                        className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                        style={{
                          fontSize: "24px",
                          width: "50px",
                          height: "50px",
                          background:
                            "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
                          color: "white",
                        }}
                      >
                        {step.step}
                      </div>
                      <div>
                        <h3 className="tc-primary head-font fs-20 mb-1">
                          {step.title}{" "}
                          <span
                            className="fs-14 fw-normal"
                            style={{ color: "#0A2458" }}
                          >
                            ({step.duration})
                          </span>
                        </h3>
                        <p
                          className="head-font2 tc-primary fs-14 mb-0"
                          style={{ color: "#0A2458" }}
                        >
                          {step.description}
                        </p>
                      </div>
                    </div>

                    <div className="mb-4">
                      <ul
                        className="head-font2 tc-primary fs-14 mb-0"
                        style={{ paddingLeft: "20px", color: "#0A2458" }}
                      >
                        {step.points.map((point, i) => (
                          <li key={i}>{point}</li>
                        ))}
                      </ul>
                    </div>

                    <div
                      className="p-3 rounded-3"
                      style={{ background: "rgba(10, 36, 88, 0.03)" }}
                    >
                      <p className="head-font2 tc-primary fs-13 fw-bold mb-0">
                        <span style={{ color: "#0a2458" }}>Deliverable:</span>{" "}
                        {step.deliverable}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-5">
              <div
                className="d-inline-flex align-items-center gap-3 p-4 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(10, 36, 88, 0.05) 0%, rgba(255, 215, 0, 0.05) 100%)",
                  border: "1px solid rgba(10, 36, 88, 0.1)",
                }}
              >
                <div style={{ fontSize: "32px" }}>🔄</div>
                <div className="text-start">
                  <p
                    className="head-font2 tc-primary fs-16 mb-0"
                    style={{ color: "#0A2458" }}
                  >
                    <strong>Post-Launch Support:</strong> Close monitoring,
                    quick bug fixes, user feedback collection, and ongoing
                    maintenance available.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CASE STUDY */}
        <section
          style={{
            padding: "100px 0",
            background: "linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%)",
          }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(10, 36, 88, 0.1)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                CASE STUDY
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Success Story: Custom Client Portal for Professional Services
              </h2>
            </div>

            <div className="row g-5">
              <div className="col-lg-6">
                <div className="position-relative">
                  <img
                    src={images?.clientPortalCase || images?.aboutUs}
                    className="img-fluid rounded-4 w-100"
                    alt="Client Portal Case Study"
                    style={{
                      boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    }}
                  />
                </div>
              </div>

              <div className="col-lg-6">
                <div className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        background: "#0a2458",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <h3 className="head-font2 tc-primary fs-18 fw-bold mb-0">
                      The Client:
                    </h3>
                  </div>
                  <p
                    className="head-font2 tc-primary fs-16 ps-3"
                    style={{ color: "#0A2458" }}
                  >
                    Professional services firm in Singapore serving corporate
                    clients across Asia
                  </p>
                </div>

                <div className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        background: "#0a2458",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <h3 className="head-font2 tc-primary fs-18 fw-bold mb-0">
                      The Challenge:
                    </h3>
                  </div>
                  <ul
                    className="head-font2 tc-primary fs-16 ps-3 mb-0"
                    style={{ color: "#0A2458" }}
                  >
                    <li>
                      Clients constantly emailing for project updates,
                      documents, invoices
                    </li>
                    <li>No centralized communication platform</li>
                    <li>Security concerns with email document sharing</li>
                    <li>Team spending 10+ hours/week on status requests</li>
                  </ul>
                </div>

                <div className="mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div
                      style={{
                        width: "8px",
                        height: "8px",
                        background: "#0a2458",
                        borderRadius: "50%",
                      }}
                    ></div>
                    <h3 className="head-font2 tc-primary fs-18 fw-bold mb-0">
                      Our Solution:
                    </h3>
                  </div>
                  <p
                    className="head-font2 tc-primary fs-16 ps-3 mb-2"
                    style={{ color: "#0A2458" }}
                  >
                    Custom client portal where clients can:
                  </p>
                  <div className="ps-3">
                    {[
                      "View project status in real-time",
                      "Access all documents securely",
                      "Download invoices & reports anytime",
                      "Communicate directly with project managers",
                      "Submit requests through structured forms",
                      "Mobile-friendly for on-the-go access",
                    ].map((feature, idx) => (
                      <div
                        key={idx}
                        className="d-flex align-items-center gap-2 mb-2"
                      >
                        <div style={{ color: "#10B981", fontSize: "16px" }}>
                          ✓
                        </div>
                        <p
                          className="head-font2 tc-primary fs-14 mb-0"
                          style={{ color: "#0A2458" }}
                        >
                          {feature}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                <div
                  className="mb-4 p-4 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #10b981 0%, #34d399 100%)",
                    color: "white",
                  }}
                >
                  <h3 className="head-font fs-20 mb-3">The Results:</h3>
                  <div className="row g-3">
                    {[
                      {
                        value: "80%",
                        label: "Reduction in document request emails",
                      },
                      { value: "10 hrs/wk", label: "Saved on status updates" },
                      {
                        value: "35%",
                        label: "Increase in client satisfaction",
                      },
                      {
                        value: "New revenue",
                        label: "Portal as premium service tier",
                      },
                    ].map((result, idx) => (
                      <div key={idx} className="col-6 text-center">
                        <div className="fw-bold fs-24">{result.value}</div>
                        <div className="fs-12 opacity-90">{result.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-4 d-flex flex-wrap gap-3">
                  <button
                    onClick={() =>
                      window.open(
                        "https://topmate.io/pluralcode/1677497",
                        "_blank",
                      )
                    }
                    className="btn px-4 py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
                      border: "none",
                      color: "white",
                      fontWeight: "600",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 10px 20px rgba(10, 36, 88, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Build Your Custom Portal
                  </button>
                  <button
                    onClick={() => window.open("/case-studies", "_blank")}
                    className="btn px-4 py-3"
                    style={{
                      background: "transparent",
                      border: "2px solid #0a2458",
                      color: "#0a2458",
                      fontWeight: "600",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#0a2458";
                      e.target.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "transparent";
                      e.target.style.color = "#0a2458";
                    }}
                  >
                    See More Case Studies
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TECHNOLOGIES */}
        <section style={{ padding: "100px 0", background: "#FFFFFF" }}>
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(10, 36, 88, 0.1)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                TECHNOLOGY STACK
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Modern, Proven Technology Stack
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                We build web applications with technologies that ensure speed,
                security, and scalability.
              </p>
            </div>

            <div className="row g-4">
              {technologies.map((tech, index) => (
                <div className="col-lg-3 col-md-6" key={index}>
                  <div
                    className="h-100 p-4 rounded-4 text-center"
                    style={{
                      background:
                        "linear-gradient(135deg, #F8FAFF 0%, #F0F7FF 100%)",
                      border: "1px solid rgba(10, 36, 88, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 12px 24px rgba(10, 36, 88, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <div
                      className="mx-auto mb-4 rounded-3 d-inline-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "32px",
                        width: "60px",
                        height: "60px",
                        background: "rgba(10, 36, 88, 0.1)",
                      }}
                    >
                      {tech.icon}
                    </div>
                    <h3 className="tc-primary head-font fs-18 mb-3">
                      {tech.category}
                    </h3>
                    <ul
                      className="head-font2 tc-primary fs-14 mb-0"
                      style={{
                        paddingLeft: "0",
                        listStyle: "none",
                        color: "#0A2458",
                      }}
                    >
                      {tech.tech.map((item, i) => (
                        <li key={i} className="mb-2">
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-5">
              <div
                className="d-inline-flex align-items-center justify-content-center gap-4 p-4 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(10, 36, 88, 0.05) 0%, rgba(255, 215, 0, 0.05) 100%)",
                  border: "1px solid rgba(10, 36, 88, 0.1)",
                }}
              >
                {[
                  {
                    icon: "✅",
                    text: "Battle-tested by thousands of companies",
                  },
                  {
                    icon: "✅",
                    text: "Active community & constant improvements",
                  },
                  { icon: "✅", text: "Scalable from 10 to 10,000+ users" },
                  { icon: "✅", text: "Cost-effective open source solutions" },
                ].map((item, index) => (
                  <div key={index} className="d-flex align-items-center gap-2">
                    <div style={{ fontSize: "20px", color: "#0a2458" }}>
                      {item.icon}
                    </div>
                    <p
                      className="head-font2 fs-14 mb-0"
                      style={{ color: "#0A2458" }}
                    >
                      {item.text}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* WEB APP VS WEBSITE */}
        <section
          style={{
            padding: "100px 0",
            background: "linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%)",
          }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(10, 36, 88, 0.1)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                DIFFERENCE
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Web Application vs Website
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                Understanding the key differences helps you make the right
                choice for your business needs.
              </p>
            </div>

            <div className="row g-4">
              <div className="col-lg-6">
                <div
                  className="h-100 p-4 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)",
                    border: "1px solid #4caf50",
                    position: "relative",
                  }}
                >
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div
                      className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "32px",
                        width: "60px",
                        height: "60px",
                        background: "#4caf50",
                        color: "white",
                      }}
                    >
                      🌐
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-0">
                      You need a WEB APPLICATION if:
                    </h3>
                  </div>
                  <ul
                    className="head-font2 tc-primary fs-16 mb-0"
                    style={{ paddingLeft: "20px", color: "#2e7d32" }}
                  >
                    <li>Users need to log in with accounts</li>
                    <li>People need to create/manage data</li>
                    <li>Multiple users collaborate</li>
                    <li>You're building a tool or platform</li>
                    <li>Transactions or workflows are involved</li>
                    <li>Real-time updates are needed</li>
                  </ul>
                </div>
              </div>

              <div className="col-lg-6">
                <div
                  className="h-100 p-4 rounded-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)",
                    border: "1px solid #ff9800",
                    position: "relative",
                  }}
                >
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div
                      className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "32px",
                        width: "60px",
                        height: "60px",
                        background: "#ff9800",
                        color: "white",
                      }}
                    >
                      📄
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-0">
                      A WEBSITE is sufficient if:
                    </h3>
                  </div>
                  <ul
                    className="head-font2 tc-primary fs-16 mb-0"
                    style={{ paddingLeft: "20px", color: "#ef6c00" }}
                  >
                    <li>Just providing information</li>
                    <li>Marketing/branding focus</li>
                    <li>No user accounts needed</li>
                    <li>Content is mostly static</li>
                    <li>Simple contact forms only</li>
                    <li>Basic online presence</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <div
                className="p-4 rounded-4"
                style={{
                  background: "rgba(10, 36, 88, 0.03)",
                  border: "1px solid rgba(10, 36, 88, 0.1)",
                }}
              >
                <p
                  className="head-font2 tc-primary fs-16 text-center mb-0"
                  style={{ color: "#0A2458" }}
                >
                  <strong>Not sure which one you need?</strong> Most businesses
                  start with a website for marketing and add web applications as
                  they grow. We can help you plan the right approach.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* INVESTMENT & TIMELINE */}
        <section style={{ padding: "100px 0", background: "#FFFFFF" }}>
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(255, 215, 0, 0.15)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                INVESTMENT
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Timeline & Investment
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                Transparent pricing and realistic timelines for custom web
                application development.
              </p>
            </div>

            <div className="row g-4 mb-5">
              <div className="col-lg-6">
                <div
                  className="h-100 p-4 rounded-4"
                  style={{
                    background: "white",
                    border: "1px solid rgba(10, 36, 88, 0.1)",
                    boxShadow: "0 8px 16px rgba(10, 36, 88, 0.08)",
                  }}
                >
                  <h3 className="tc-primary head-font fs-24 mb-4">
                    Typical Timeline:
                  </h3>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        className="head-font2 fs-16 fw-bold"
                        style={{ color: "#0a2458" }}
                      >
                        Simple web apps
                      </span>
                      <span
                        className="head-font2 fs-16 fw-bold"
                        style={{ color: "#10B981" }}
                      >
                        2-3 months
                      </span>
                    </div>
                    <p
                      className="head-font2 fs-14 mb-0"
                      style={{ color: "#0A2458", fontStyle: "italic" }}
                    >
                      Example: Basic customer portal with document access and
                      messaging
                    </p>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        className="head-font2 fs-16 fw-bold"
                        style={{ color: "#0a2458" }}
                      >
                        Medium complexity
                      </span>
                      <span
                        className="head-font2 fs-16 fw-bold"
                        style={{ color: "#F59E0B" }}
                      >
                        3-5 months
                      </span>
                    </div>
                    <p
                      className="head-font2 fs-14 mb-0"
                      style={{ color: "#0A2458", fontStyle: "italic" }}
                    >
                      Example: Booking platform with payments, calendar, user
                      management
                    </p>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span
                        className="head-font2 fs-16 fw-bold"
                        style={{ color: "#0a2458" }}
                      >
                        Complex applications
                      </span>
                      <span
                        className="head-font2 fs-16 fw-bold"
                        style={{ color: "#EF4444" }}
                      >
                        5-9 months
                      </span>
                    </div>
                    <p
                      className="head-font2 fs-14 mb-0"
                      style={{ color: "#0A2458", fontStyle: "italic" }}
                    >
                      Example: Multi-tenant SaaS platform with advanced features
                    </p>
                  </div>
                </div>
              </div>

              <div className="col-lg-6">
                <div
                  className="h-100 p-4 rounded-4"
                  style={{
                    background: "white",
                    border: "1px solid rgba(10, 36, 88, 0.1)",
                    boxShadow: "0 8px 16px rgba(10, 36, 88, 0.08)",
                  }}
                >
                  <h3 className="tc-primary head-font fs-24 mb-4">
                    Investment Considerations:
                  </h3>
                  <div className="mb-3">
                    <p
                      className="head-font2 tc-primary fs-16 fw-bold mb-2"
                      style={{ color: "#0a2458" }}
                    >
                      Scope & Features
                    </p>
                    <ul
                      className="head-font2 tc-primary fs-14 mb-0"
                      style={{ paddingLeft: "20px", color: "#0A2458" }}
                    >
                      <li>Number of user types/roles</li>
                      <li>Features & functionality depth</li>
                      <li>Admin panel complexity</li>
                    </ul>
                  </div>
                  <div className="mb-3">
                    <p
                      className="head-font2 tc-primary fs-16 fw-bold mb-2"
                      style={{ color: "#0a2458" }}
                    >
                      Design Complexity
                    </p>
                    <ul
                      className="head-font2 tc-primary fs-14 mb-0"
                      style={{ paddingLeft: "20px", color: "#0A2458" }}
                    >
                      <li>Custom UI/UX design</li>
                      <li>Number of unique screens</li>
                      <li>Responsive requirements</li>
                    </ul>
                  </div>
                  <div className="mb-3">
                    <p
                      className="head-font2 tc-primary fs-16 fw-bold mb-2"
                      style={{ color: "#0a2458" }}
                    >
                      Integrations & Data
                    </p>
                    <ul
                      className="head-font2 tc-primary fs-14 mb-0"
                      style={{ paddingLeft: "20px", color: "#0A2458" }}
                    >
                      <li>Third-party services</li>
                      <li>Existing system connections</li>
                      <li>Database structure complexity</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center">
              <div
                className="p-5 rounded-4"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(10, 36, 88, 0.05) 0%, rgba(10, 36, 88, 0.01) 100%)",
                  border: "1px solid rgba(10, 36, 88, 0.1)",
                }}
              >
                <h3 className="tc-primary head-font fs-28 mb-4">
                  Typical Investment Range
                </h3>
                <div className="row justify-content-center">
                  {[
                    {
                      type: "Basic web apps",
                      price: "$2,500 - $4,000",
                      color: "#10B981",
                    },
                    {
                      type: "Medium complexity",
                      price: "$4,000 - $6,000",
                      color: "#F59E0B",
                    },
                    {
                      type: "Complex platforms",
                      price: "$6,000 - $10,000+",
                      color: "#EF4444",
                    },
                  ].map((item, index) => (
                    <div className="col-md-4 mb-4" key={index}>
                      <div
                        className="p-4 rounded-4 h-100"
                        style={{
                          background: "white",
                          border: `2px solid ${item.color}`,
                          boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                        }}
                      >
                        <p className="head-font2 tc-primary fs-18 fw-bold mb-2">
                          {item.type}
                        </p>
                        {/* <p
                          className="head-font fs-32 fw-bold mb-3"
                          style={{ color: item.color }}
                        >
                          {item.price}
                        </p> */}
                        <p
                          className="head-font2 tc-primary fs-14 mb-0"
                          style={{ color: "#0A2458" }}
                        >
                          Includes design, development, testing, and deployment
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="head-font2 tc-primary fs-17 mt-4 mb-4">
                  <strong>
                    Exact pricing provided after consultation and requirements
                    review.
                  </strong>
                </p>
                <button
                  onClick={() => window.open("/lm/roi-calculator", "_blank")}
                  className="btn btn-lg px-5 py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: "600",
                    borderRadius: "12px",
                    transition: "all 0.3s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-2px)";
                    e.target.style.boxShadow =
                      "0 15px 30px rgba(10, 36, 88, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "none";
                  }}
                >
                  Calculate Your ROI →
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section
          style={{
            padding: "100px 0",
            background: "linear-gradient(180deg, #F0F7FF 0%, #FFFFFF 100%)",
          }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(10, 36, 88, 0.1)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                FAQ
              </span>
              <h2
                className="tc-primary head-font mb-4"
                style={{ fontSize: "clamp(2rem, 4vw, 2.75rem)" }}
              >
                Frequently Asked Questions
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "700px", color: "#0A2458" }}
              >
                Get answers to common questions about custom web application
                development.
              </p>
            </div>

            <div className="row">
              <div className="col-lg-10 mx-auto">
                <div className="accordion" id="faqAccordion">
                  {faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="accordion-item mb-3 border-0"
                      style={{ background: "transparent" }}
                    >
                      <h2 className="accordion-header">
                        <button
                          className="accordion-button collapsed p-4 rounded-3"
                          type="button"
                          data-bs-toggle="collapse"
                          data-bs-target={`#faq${index}`}
                          style={{
                            background: "white",
                            border: "1px solid rgba(10, 36, 88, 0.1)",
                            boxShadow: "0 4px 6px rgba(10, 36, 88, 0.05)",
                            color: "#0a2458",
                            fontWeight: "600",
                          }}
                        >
                          {faq.question}
                        </button>
                      </h2>
                      <div
                        id={`faq${index}`}
                        className="accordion-collapse collapse"
                        data-bs-parent="#faqAccordion"
                      >
                        <div
                          className="accordion-body p-4 rounded-bottom-3"
                          style={{
                            background: "white",
                            border: "1px solid rgba(10, 36, 88, 0.1)",
                            borderTop: "none",
                          }}
                        >
                          <p
                            className="head-font2 tc-primary fs-16 mb-0"
                            style={{ color: "#0A2458" }}
                          >
                            {faq.answer}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FINAL CTA */}
        <section
          style={{
            padding: "100px 0",
            background: "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Background pattern */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `radial-gradient(circle at 25% 25%, rgba(255, 215, 0, 0.1) 2px, transparent 2px)`,
              backgroundSize: "40px 40px",
            }}
          />

          <div className="container position-relative" style={{ zIndex: 1 }}>
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "white",
                  fontWeight: "600",
                  fontSize: "14px",
                  backdropFilter: "blur(10px)",
                }}
              >
                GET STARTED
              </span>
              <h2 className="head-font fs-44 mb-4" style={{ color: "white" }}>
                Ready to Build Your Web Application?
              </h2>
              <p
                className="head-font2 fs-20 mb-5 mx-auto"
                style={{
                  color: "rgba(255, 255, 255, 0.9)",
                  maxWidth: "700px",
                }}
              >
                Let's discuss your idea, challenges, and goals. We'll help you
                determine the best approach for your business.
              </p>
            </div>

            <div className="row g-4 justify-content-center">
              <div className="col-lg-4 col-md-6">
                <div
                  className="p-4 h-100 rounded-4"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div
                      className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "32px",
                        width: "60px",
                        height: "60px",
                        background: "rgba(10, 36, 88, 0.1)",
                      }}
                    >
                      💬
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-0">
                      Free Consultation
                    </h3>
                  </div>
                  <p
                    className="head-font2 tc-primary fs-16 mb-4"
                    style={{ color: "#0A2458" }}
                  >
                    30-minute call to discuss your web app concept and
                    requirements
                  </p>
                  <button
                    onClick={() =>
                      window.open(
                        "https://topmate.io/pluralcode/1677497",
                        "_blank",
                      )
                    }
                    className="btn btn-lg w-100 py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
                      border: "none",
                      color: "white",
                      fontWeight: "600",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 10px 20px rgba(10, 36, 88, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Schedule Free Call →
                  </button>
                </div>
              </div>

              <div className="col-lg-4 col-md-6">
                <div
                  className="p-4 h-100 rounded-4"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    position: "relative",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: "-10px",
                      right: "20px",
                      background: "#FFD700",
                      color: "#0a2458",
                      padding: "5px 15px",
                      borderRadius: "20px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    MOST POPULAR
                  </div>

                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div
                      className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "32px",
                        width: "60px",
                        height: "60px",
                        background: "rgba(10, 36, 88, 0.1)",
                      }}
                    >
                      📋
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-0">
                      Project Inquiry
                    </h3>
                  </div>
                  <p
                    className="head-font2 tc-primary fs-16 mb-4"
                    style={{ color: "#0A2458" }}
                  >
                    Send us detailed requirements for a customized proposal
                  </p>
                  <button
                    onClick={() =>
                      window.open(
                        "https://topmate.io/pluralcode/1677497?type=project",
                        "_blank",
                      )
                    }
                    className="btn btn-lg w-100 py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #FFD700 0%, #FFC107 100%)",
                      border: "none",
                      color: "#0a2458",
                      fontWeight: "600",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 10px 20px rgba(255, 215, 0, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Submit Project Details →
                  </button>
                </div>
              </div>

              <div className="col-lg-4 col-md-6">
                <div
                  className="p-4 h-100 rounded-4"
                  style={{
                    background: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(10px)",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                  }}
                >
                  <div className="d-flex align-items-center gap-3 mb-4">
                    <div
                      className="flex-shrink-0 rounded-3 d-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "32px",
                        width: "60px",
                        height: "60px",
                        background: "rgba(10, 36, 88, 0.1)",
                      }}
                    >
                      📚
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-0">
                      Explore Resources
                    </h3>
                  </div>
                  <p
                    className="head-font2 tc-primary fs-16 mb-4"
                    style={{ color: "#0A2458" }}
                  >
                    Access free tools, templates, and case studies
                  </p>
                  <button
                    onClick={() => window.open("/lm/tools", "_blank")}
                    className="btn btn-lg w-100 py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, #0a2458 0%, #1a3b8a 100%)",
                      border: "none",
                      color: "white",
                      fontWeight: "600",
                      borderRadius: "8px",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow =
                        "0 10px 20px rgba(10, 36, 88, 0.3)";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "none";
                    }}
                  >
                    Explore Free Tools →
                  </button>
                </div>
              </div>
            </div>

            <div className="text-center mt-5 pt-5">
              <div
                className="p-4 rounded-4"
                style={{
                  background: "rgba(255, 255, 255, 0.1)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(10px)",
                }}
              >
                <p
                  className="head-font2 fs-17 mb-0"
                  style={{ color: "rgba(255, 255, 255, 0.9)" }}
                >
                  📧 <strong>Email:</strong> info@pluralcode.com | 📞{" "}
                  <strong>Phone:</strong> +91 9958920231
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* RELATED SERVICES */}
        <section
          style={{
            padding: "80px 0",
            background: "linear-gradient(180deg, #F8FAFF 0%, #FFFFFF 100%)",
          }}
        >
          <div className="container">
            <div className="text-center mb-5">
              <span
                className="px-3 py-1 rounded-pill d-inline-block mb-3"
                style={{
                  background: "rgba(10, 36, 88, 0.1)",
                  color: "#0a2458",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                RELATED SERVICES
              </span>
              <h2 className="tc-primary head-font fs-36 mb-4">
                You Might Also Need
              </h2>
              <p
                className="head-font2 tc-primary fs-18 mx-auto"
                style={{ maxWidth: "600px", color: "#0A2458" }}
              >
                Complement your web application with these related services
              </p>
            </div>

            <div className="row g-4">
              {[
                {
                  icon: "🏢",
                  title: "Custom Business Software",
                  description:
                    "Need internal process management or operations software? We build that too.",
                  link: "/services/custom-business-software",
                },
                {
                  icon: "📱",
                  title: "Mobile App Development",
                  description:
                    "Want a native mobile app alongside your web app? We do both.",
                  link: "/services/mobile-application-development",
                },
                {
                  icon: "🔗",
                  title: "System Integration",
                  description:
                    "Need your web app to connect with existing tools? We specialize in integrations.",
                  link: "/services/integration",
                },
              ].map((service, index) => (
                <div className="col-lg-4 col-md-6" key={index}>
                  <div
                    className="p-4 h-100 rounded-4 text-center"
                    style={{
                      background: "white",
                      border: "1px solid rgba(10, 36, 88, 0.1)",
                      boxShadow: "0 8px 16px rgba(10, 36, 88, 0.08)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-5px)";
                      e.currentTarget.style.boxShadow =
                        "0 16px 32px rgba(10, 36, 88, 0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 16px rgba(10, 36, 88, 0.08)";
                    }}
                  >
                    <div
                      className="mx-auto mb-4 rounded-3 d-inline-flex align-items-center justify-content-center"
                      style={{
                        fontSize: "48px",
                        width: "80px",
                        height: "80px",
                        background: "rgba(10, 36, 88, 0.1)",
                      }}
                    >
                      {service.icon}
                    </div>
                    <h3 className="tc-primary head-font fs-20 mb-3">
                      {service.title}
                    </h3>
                    <p
                      className="head-font2 tc-primary fs-16 mb-4"
                      style={{ color: "#0A2458" }}
                    >
                      {service.description}
                    </p>
                    <button
                      onClick={() => window.open(service.link, "_blank")}
                      className="btn px-4 py-2"
                      style={{
                        background: "transparent",
                        border: "2px solid #0a2458",
                        color: "#0a2458",
                        fontWeight: "600",
                        borderRadius: "8px",
                        transition: "all 0.3s ease",
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = "#0a2458";
                        e.target.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = "transparent";
                        e.target.style.color = "#0a2458";
                      }}
                    >
                      Learn More →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <style jsx>{`
        .min-vh-90 {
          min-height: 90vh;
        }

        @media (max-width: 768px) {
          .display-3 {
            font-size: 2.5rem !important;
          }

          .min-vh-90 {
            min-height: auto;
            padding: 80px 0;
          }
        }
      `}</style>
    </div>
  );
}
